import groupBy from "lodash/groupBy";
import uniq from "lodash/uniq";

import { supabase } from "@/lib/supabase";
import { throwExpression } from "@/utils/util";
import { CADCurrencyConverter } from "./currency";
import { Item, itemSelect, processItem } from "./items";
import { PageableRpcReturnData, supabasePagedRPC } from "@/lib/supabase/supabase-rpc";

type SupabaseBOMRecord = PageableRpcReturnData<"getExpandedBOMHierarchy">;

export interface BomRecordsForParents {
  parents: { item: Item; quantity: number }[];
  bomRecordsByParentPath: Record<string, BomRecord[]>;
  itemsById: Map<number, Item>;
}

export interface BomRecord extends SupabaseBOMRecord {
  item: Item;
}

export async function fetchBomRecordsForParents(
  parents: { item: Item; quantity: number }[],
  cadCurrencyConverter: CADCurrencyConverter,
): Promise<BomRecordsForParents> {
  // Do nothing if no parents were specified
  if (!parents.length) return { parents, bomRecordsByParentPath: {}, itemsById: new Map() };

  // Fetch BOM records for all assemblies
  const assemblyIds = uniq(parents.filter((x) => x.item.type === "Assembly").map((x) => x.item.id));
  const supabaseBomRecords = await supabasePagedRPC("getExpandedBOMHierarchy", { assemblyIds });
  console.info(
    `Fetched ${supabaseBomRecords.length.toFixed()} expanded BOM records for ${assemblyIds.length.toFixed()} assemblies from ${parents.length.toFixed()} parent line items`,
  );

  // Fetch item records for all items in the BOM
  const parentIds = new Set(parents.map((x) => x.item.id));
  const componentIds = uniq(supabaseBomRecords.map((x) => x.componentId)).filter((id) => !parentIds.has(id));
  console.info(`Fetching ${componentIds.length.toFixed()} BOM component item records`);
  const rawComponents = (await supabase.from("Item").select(itemSelect).in("id", componentIds).throwOnError()).data;
  const components = rawComponents.map((x) => processItem(x, cadCurrencyConverter));
  const allItems = parents.map((x) => x.item).concat(components);
  const itemsById = new Map(allItems.map((x) => [x.id, x]));

  // Add item details to each BOM record
  const bomRecords = supabaseBomRecords.map((record) => ({
    ...record,
    item:
      itemsById.get(record.componentId) ??
      throwExpression(`Component with ID ${record.componentId.toFixed()} not found while expanding BOM`),
  }));

  // Group the BOM records by path
  const bomRecordsByParentPath = groupBy(bomRecords, (x) => x.path.join(","));

  return { parents, bomRecordsByParentPath, itemsById };
}
